import { LocalDate, Month } from "@js-joda/core";
import currency from "currency.js";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { makeEncoderFromCodec, makeRoute } from "trader.ts/route";
import { GetReturnTypeFromApiSpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dyn } from "trader.ts/ui";
import { Form, selectBox } from "trader.ts/ui.forms";
import { checkAllCasesHandled, tryExtractErrorMessage } from "trader.ts/utils";
import { P, match } from "ts-pattern";
import {
  expectedpaymentid,
  internalteacherid,
  invoiceid,
  thirdpartysellerid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeClientsideRouter } from "../../client/clientsiderouter";
import { renderTableDownloadLinks } from "../../client/downloadtable";
import {
  renderCancelButton,
  renderErrorPanel,
  renderSaveButton,
  renderSuccessPanel,
} from "../../client/savebutton";
import { isEqual } from "../../lib/isequal";
import { isNil } from "../../lib/istype";
import { dateInDaterange } from "../../lib/time";
import {
  ddMMyyyy,
  renderCurrency,
  renderedInvoiceSubjectToHtml,
  renderFirstNameLastName,
  renderInvoiceSubjectForOverviews,
} from "../../render";
import * as routes from "../../routes";
import { translateMonth } from "../../translations_specific";
import { schoolurl } from "../../types";
import {
  invoiceSubjectFilterField,
  renderReportResult,
  runQueryOnFormChange,
} from "../finance_invoices/client";
import { thirdPartySellerField } from "../payments_overview_non_periodic/client";

const route = makeRoute("/filters/{mode:mode}", {
  mode: makeEncoderFromCodec(
    routes.magicCodec<{
      month: [number, Month];
      paid: "All" | "OnlyUnpaid";
      thirdpartyseller:
        | { tag: "no_filter" }
        | { tag: "none" }
        | {
            tag: "thirdpartyseller";
            item: { id: thirdpartysellerid; name: string };
          };
    }>(),
  ),
});

export function render(opts: {
  schoolurl: schoolurl;
  months: [number, Month][];
  thirdPartySellers: { id: thirdpartysellerid; name: string }[];
  electronicPaymentsEnabled: boolean;
  hasPeriodFees: boolean;
  internalTeachers: {
    firstname: string;
    lastname: string;
    internalteacherid: internalteacherid;
  }[];
}): HTMLElement | HTMLElement[] {
  const today = LocalDate.now();
  const currentMonth = opts.months.find((m) =>
    dateInDaterange(
      { date: today },
      {
        from: LocalDate.of(m[0], m[1], 1),
        until: LocalDate.of(m[0], m[1], 1).plusMonths(1).minusDays(1),
      },
    ),
  );

  const router = makeClientsideRouter(route, {
    month: currentMonth || opts.months[0],
    paid: "OnlyUnpaid" as const,
    thirdpartyseller: { tag: "none" },
  });
  const s = router.start();

  const form = new Form({})
    .addField("month", [], () =>
      selectBox({
        values: opts.months,
        initial: s.get().month,
        show: (s) => translateMonth(s[1]) + " " + s[0],
        label: "Maand",
      }),
    )
    .addField("paid", [], async () =>
      selectBox({
        values: ["OnlyUnpaid" as const, "All" as const],
        initial: s.get().paid,
        show: (s) =>
          s === "All"
            ? "Allemaal"
            : s === "OnlyUnpaid"
            ? "Alleen onbetaalde"
            : checkAllCasesHandled(s),
        // render: radioFieldCheckboxRender,
        label: "Betaalstatus",
      }),
    )
    .addField("thirdpartyseller", [], async () => {
      return thirdPartySellerField({
        thirdPartySellers: opts.thirdPartySellers,
        initialValue: s.get().thirdpartyseller,
      });
    })
    .addField("periodic_payment_method", [], () => {
      const values = [
        "no_filter" as const,
        "direct_debit" as const,
        "manual" as const,
      ];
      return selectBox({
        values: values,
        initial: values[0],
        show: (t) =>
          t === "no_filter"
            ? "Allemaal"
            : t === "direct_debit"
            ? "Incasso"
            : t === "manual"
            ? "Manueel"
            : checkAllCasesHandled(t),
        label: "Incasso / manueel",
      });
    })
    .addField("subject", [], async () => invoiceSubjectFilterField(opts))
    .build();

  form.s.observe((curr) => {
    if (curr.tag === "parsed") {
      document.location.hash = route.mkClientSideLinkForHref({
        mode: curr.parsed,
      });
    }
  });

  const resultS = runQueryOnFormChange(
    form.s,
    routes.queryPeriodPaymentsStatisticSpec,
    (t) => t,
  );

  const tableid = "classy-table-id";

  const downloadLinks = renderTableDownloadLinks({
    tableid,
    filename: "OverzichtPeriodiekeBetalingen",
  });

  const errS = new Source(<span></span>);
  const successS = new Source(<span></span>);

  return (
    <main style="max-width: unset;">
      <section className={style.equal_columns + style.cancel_main_margins}>
        {form.render()}
      </section>
      {renderReportResult(resultS, (rows) => {
        const selectingS: Source<
          null | "manual" | "direct_debit" | "direct_debit_announcement"
        > = new Source(null);

        const selectedS: Source<
          { invoiceid: invoiceid; expectedpaymentid: expectedpaymentid }[]
        > = new Source([]);

        const hasDirectDebitEps = rows.some(
          (r) => r.periodic_payment_method === "direct_debit",
        );
        const hasEmailReminderEps = rows.some(
          (r) => r.periodic_payment_method === "manual",
        );

        return dyn(selectingS, (selecting) => {
          const actions = dyn(selectedS, (selected) => {
            const selCount = selected.length;
            return (
              <div style="text-align: right">
                {selecting !== null ? (
                  [
                    renderCancelButton({ onclick: () => selectingS.set(null) }),
                    renderSaveButton({
                      save: async () => {
                        errS.set(<span></span>);
                        successS.set(<span></span>);
                        const rpcRes = await rpc(
                          routes.sendPaymentRemindersAndDirectDebitsPeriodicSpec,
                          undefined,
                          { tag: selecting, ids: selectedS.get() },
                          { timeoutMS: 60000 },
                        )
                          .then((res) => {
                            successS.set(
                              renderSuccessPanel(
                                <div>
                                  <div>
                                    <b>{"Gelukt!"}</b>
                                  </div>
                                  {res.direct_debit_transaction_ids.length >
                                  0 ? (
                                    <div>
                                      <div>{"Aangemaakt: "}</div>
                                      {res.direct_debit_transaction_ids.map(
                                        (id) => (
                                          <div>
                                            <a href={id.dashboardurl || ""}>
                                              {id.paymentid}
                                            </a>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  ) : (
                                    <span></span>
                                  )}
                                </div>,
                              ),
                            );
                          })
                          .catch((err) => {
                            // Capture error here,
                            // otherwise we recalc the result and the error disappears
                            errS.set(
                              renderErrorPanel({
                                error: tryExtractErrorMessage(err),
                              }),
                            );
                            form.s.set(form.s.get());
                            throw err;
                          });
                        return rpcRes;
                      },
                      text: {
                        normal: match(selecting)
                          .with(
                            P.union("manual", "direct_debit_announcement"),
                            () =>
                              selCount > 0
                                ? "Zet %n emails in de wachtrij. ".replace("%n", selCount.toString())
                                : "",
                          )
                          .with("direct_debit", () =>
                            selCount > 0
                              ? "Stuur %n incasso's naar Mollie.".replace("%n", selCount.toString())
                              : "",
                          )
                          .exhaustive(),
                        success: "Gelukt!",
                      },
                      timeout: 1,
                      reload: () => form.s.set(form.s.get()),
                    }),
                  ]
                ) : (
                  <div>
                    {hasEmailReminderEps ? (
                      <button
                        className={style.button_accent}
                        onclick={() => selectingS.set("manual")}
                      >
                        <i className={FA.icon_envelope_solid}></i>
                        {"Stuur emails met betalingslink"}
                      </button>
                    ) : (
                      <span></span>
                    )}
                    {hasDirectDebitEps
                      ? [
                          <button
                            onclick={() =>
                              selectingS.set("direct_debit_announcement")
                            }
                          >
                            <i className={FA.icon_envelope_solid}></i>
                            {hasDirectDebitEps
                              ? "Stuur aankondigingemails voor incasso's"
                              : ""}
                          </button>,
                          <button
                            className={style.button_accent}
                            onclick={() => selectingS.set("direct_debit")}
                          >
                            <i className={FA.icon_euro_sign_solid_1}></i>
                            {hasDirectDebitEps
                              ? "Stuur incasso's"
                              : ""}
                          </button>,
                        ]
                      : []}
                  </div>
                )}
              </div>
            );
          });
          const headerRow = (
            <tr style="position: sticky; top: 0px; background-color: white;">
              <th>{"Student"}</th>
              <th>{"Les"}</th>
              <th>{"Betalingsdatum"}</th>
              <th>
                {"Laatste %newline herinnering".replace(
                  "%newline",
                  "\n",
                )}
              </th>
              <th>{"Bedrag"}</th>
              <th>{"Betaald"}</th>
              {selecting !== null ? (
                <th>
                  <a
                    onclick={() => {
                      const curr = selectedS.get();
                      const selectableRows = rows.filter(
                        (r) =>
                          getIsSelectionPossible(opts, selecting, r).tag ===
                          true,
                      );
                      if (curr.length === selectableRows.length) {
                        selectedS.set([]);
                      } else {
                        selectedS.set(
                          selectableRows.map((r) => ({
                            invoiceid: r.invoiceid,
                            expectedpaymentid: r.id,
                          })),
                        );
                      }
                    }}
                  >
                    {"Alles selecteren"}
                  </a>
                </th>
              ) : (
                []
              )}
            </tr>
          );
          const renderedRows =
            rows === null
              ? [<tr></tr>]
              : rows
                  .orderBy((r) => renderFirstNameLastName(r.subject.student))
                  .map((row) => {
                    return (
                      <tr>
                        <td>
                          <a
                            href={routes.studentOverviewRoute.link({
                              studentid: row.subject.student.id,
                            })}
                          >
                            {renderFirstNameLastName(row.subject.student)}
                          </a>
                        </td>
                        <td>
                          {renderedInvoiceSubjectToHtml(
                            renderInvoiceSubjectForOverviews(row.subject),
                          )}
                        </td>
                        <td>{row.date.format(ddMMyyyy)}</td>
                        <td>
                          {row.last_reminder_sent?.format(ddMMyyyy) || ""}
                        </td>
                        <td>
                          <a
                            href={routes.invoicedetailsRoute.link({
                              invoiceid: row.invoiceid,
                              edit: false,
                            })}
                          >
                            {renderCurrency(row.amount)}
                          </a>
                        </td>
                        {isNil(row.pending_online_payment.id) ? (
                          <td
                            style={`background-color: ${
                              row.amount.value === row.amount_paid.value
                                ? "#b9f5a7"
                                : "#ffb48e"
                            };`}
                          >
                            <div>{renderCurrency(row.amount_paid)}</div>
                            {(row.failed_online_payments || []).length > 0 ? (
                              <div>
                                {"Niet gelukt" + ": "}
                                {(row.failed_online_payments || []).map((op) =>
                                  !isNil(op.dashboardurl) ? (
                                    <a target="_blank" href={op.dashboardurl}>
                                      {op.id}
                                    </a>
                                  ) : (
                                    <span>{op.id}</span>
                                  ),
                                )}
                              </div>
                            ) : (
                              []
                            )}
                          </td>
                        ) : (
                          <td style={`background-color: var(--grey-200);`}>
                            {"Incasso verstuurd" + " - "}
                            {row.pending_online_payment.dashboardurl ? (
                              <a
                                target="_blank"
                                href={row.pending_online_payment.dashboardurl}
                              >
                                {row.pending_online_payment.id}
                              </a>
                            ) : (
                              <span>{row.pending_online_payment.id}</span>
                            )}
                          </td>
                        )}
                        {selecting !== null
                          ? dyn(selectedS, (selected) => {
                              const isSelectionPossible =
                                getIsSelectionPossible(opts, selecting, row);
                              if (isSelectionPossible.tag === false) {
                                if (isSelectionPossible.link !== null) {
                                  return isSelectionPossible.label === null ? (
                                    <td></td>
                                  ) : (
                                    <td>
                                      <a
                                        href={isSelectionPossible.link}
                                        target="_blank"
                                      >
                                        {isSelectionPossible.label}
                                      </a>
                                    </td>
                                  );
                                } else {
                                  return isSelectionPossible.label === null ? (
                                    <td></td>
                                  ) : (
                                    <td>
                                      <div>{isSelectionPossible.label}</div>
                                    </td>
                                  );
                                }
                              } else {
                                const thisvalue = {
                                  invoiceid: row.invoiceid,
                                  expectedpaymentid: row.id,
                                };
                                return (
                                  <td>
                                    <div
                                      className={
                                        style.input_like + style.checkbox
                                      }
                                      style={
                                        "margin-bottom: 0px;" +
                                        match(selecting)
                                          .with(
                                            "direct_debit",
                                            () =>
                                              "background-color: var(--accent-100);",
                                          )
                                          .with("manual", () => "")
                                          .with(
                                            "direct_debit_announcement",
                                            () => "",
                                          )
                                          .exhaustive()
                                      }
                                      onclick={() =>
                                        selectedS.set(
                                          selected.some((r) =>
                                            isEqual(r, thisvalue),
                                          )
                                            ? selected.filter(
                                                (r) => !isEqual(r, thisvalue),
                                              )
                                            : [...selected, thisvalue],
                                        )
                                      }
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selected.some((r) =>
                                          isEqual(r, thisvalue),
                                        )}
                                      />
                                      <span>
                                        {selecting === "manual" ||
                                        selecting ===
                                          "direct_debit_announcement"
                                          ? "Email"
                                          : selecting === "direct_debit"
                                          ? "Incasso"
                                          : checkAllCasesHandled(selecting)}
                                      </span>
                                    </div>
                                  </td>
                                );
                              }
                            })
                          : []}
                      </tr>
                    );
                  });
          const renderedTotal =
            rows === null ? (
              <span></span>
            ) : (
              <tr style="font-weight: bold;background-color: var(--grey-100);">
                <td>{"Totaal" + ": " + rows.length}</td>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  {renderCurrency(
                    rows.reduce((acc, row) => acc.add(row.amount), currency(0)),
                  )}
                </td>
                <td>
                  {renderCurrency(
                    rows.reduce(
                      (acc, row) => acc.add(row.amount_paid),
                      currency(0),
                    ),
                  )}
                </td>
                {selecting !== null ? <td></td> : []}
              </tr>
            );
          return (
            <div>
              <section>
                {actions}
                {dyn(errS, (err) => err)}
                {dyn(successS, (s) => s)}
                {downloadLinks}
              </section>
              <table id={tableid}>
                {headerRow}
                {renderedRows}
                {renderedTotal}
              </table>
            </div>
          );
        });
      })}
    </main>
  );
}

function getIsSelectionPossible(
  opts: { electronicPaymentsEnabled: boolean },
  tag: "manual" | "direct_debit" | "direct_debit_announcement",
  row: GetReturnTypeFromApiSpec<
    typeof routes.queryPeriodPaymentsStatisticSpec
  >[number],
): { tag: true } | { tag: false; link: null | string; label: string | null } {
  if (tag === "manual") {
    if (row.periodic_payment_method === "direct_debit") {
      return { tag: false, link: null, label: null };
    } else {
      return { tag: true };
    }
  } else if (tag === "direct_debit" || tag === "direct_debit_announcement") {
    if (row.periodic_payment_method === "manual") {
      return { tag: false, link: null, label: null };
    } else {
      if (opts.electronicPaymentsEnabled === false) {
        return {
          tag: false,
          link: routes.config_mollieRoute.link(),
          label: "Geen connectie met Mollie",
        };
      } else if (
        row.studentExtra.iban === null ||
        row.studentExtra.iban.trim() === "" ||
        row.studentExtra.name_on_iban === null ||
        row.studentExtra.name_on_iban.trim() === ""
      ) {
        return {
          tag: false,
          link: routes.studenteditRoute.link({
            studentid: row.subject.student.id,
          }),
          label: "Geen IBAN nummer gevonden",
        };
      } else if (row.studentExtra.direct_debit_allowed === false) {
        return {
          tag: false,
          label: "Geen incasso goedkeuring",
          link: null,
        };
      } else if (!isNil(row.pending_online_payment.id)) {
        return { tag: false, label: "Incasso verstuurd", link: null };
      } else {
        return { tag: true };
      }
    }
  } else {
    return checkAllCasesHandled(tag);
  }
}
